import type { TranslationKey } from '@/lib/i18n';
import type { JurisdictionSurface } from '@/api/queries/jurisdiction';

export interface SettingsSection {
  path: string;
  labelKey: TranslationKey;
  /** Single permission required, mutually exclusive with `anyPermission`. */
  permission?: string;
  /** At least one of these permissions is required. */
  anyPermission?: string[];
  /** Hidden when the company's jurisdiction does not provide this surface. */
  surface?: JurisdictionSurface;
  /** Hidden unless the deployment has assistant commands on (COMMANDS_ENABLED). */
  requiresCommands?: true;
}

/**
 * One entry per settings section: feeds the desktop rail, the mobile
 * `<select>`, and the index redirect from a single source (router.tsx
 * gates each route with the same `permission`/`anyPermission`).
 */
export const SETTINGS_SECTIONS: SettingsSection[] = [
  { path: 'entreprise', labelKey: 'settings.nav.entreprise', permission: 'tenant:settings' },
  { path: 'modules', labelKey: 'settings.nav.modules', permission: 'tenant:settings' },
  { path: 'modules-menus', labelKey: 'settings.nav.modulesMenus', permission: 'tenant:settings' },
  { path: 'fiscalite', labelKey: 'settings.nav.fiscalite', permission: 'tenant:settings' },
  { path: 'facturation', labelKey: 'settings.nav.facturation', permission: 'tenant:settings' },
  { path: 'apparence', labelKey: 'settings.nav.apparence', permission: 'tenant:settings' },
  { path: 'fidelite', labelKey: 'settings.nav.fidelite', permission: 'tenant:settings' },
  { path: 'caisse', labelKey: 'settings.nav.caisse', permission: 'tenant:settings' },
  { path: 'tableau-de-bord', labelKey: 'settings.nav.tableauDeBord', permission: 'tenant:settings' },
  {
    path: 'comptes-bancaires',
    labelKey: 'settings.nav.comptesBancaires',
    permission: 'tenant:settings',
  },
  {
    path: 'parametrage-dgi',
    labelKey: 'settings.nav.parametrageDgi',
    anyPermission: ['tenant:settings', 'tenant:parameter-tables'],
  },
  { path: 'notifications', labelKey: 'settings.nav.notifications', permission: 'tenant:settings' },
  { path: 'mise-en-service', labelKey: 'settings.nav.miseEnService', permission: 'tenant:settings' },
  { path: 'cabinet', labelKey: 'nav.cabinetSettings', permission: 'tenant:settings' },
  { path: 'pispi', labelKey: 'settings.nav.pispi', permission: 'pispi:configure' },
  { path: 'agents-mcf', labelKey: 'settings.nav.agentsMcf', permission: 'mcf-agent:enroll', surface: 'MCF' },
  { path: 'appareils-mcf', labelKey: 'settings.nav.appareilsMcf', permission: 'mcf:gerer-appareils', surface: 'MCF' },
  { path: 'pannes-mcf', labelKey: 'settings.nav.pannesMcf', permission: 'mcf:declarer-panne', surface: 'MCF' },
  { path: 'workflow', labelKey: 'nav.workflow', permission: 'tenant:workflow-config' },
  {
    path: 'assistant-commandes',
    labelKey: 'settings.nav.assistantCommandes',
    permission: 'tenant:settings',
    requiresCommands: true,
  },
];

/** Any one of these opens the settings shell: each section's own gate. */
export const SETTINGS_PERMISSIONS: readonly string[] = [
  ...new Set(
    SETTINGS_SECTIONS.flatMap((section) =>
      section.anyPermission ?? (section.permission ? [section.permission] : [])
    )
  ),
];

/** True when the user holds the permission(s) a section requires. */
export function canSeeSection(
  section: SettingsSection,
  hasPermission: (permission: string) => boolean,
  hasAnyPermission: (...permissions: string[]) => boolean
): boolean {
  if (section.anyPermission) return hasAnyPermission(...section.anyPermission);
  if (section.permission) return hasPermission(section.permission);
  return false;
}
